// Where a person goes after signing in. A page that needs a signed-in person sends them to
// /login?redirect=<path> (the desktop app's /desktop/connect, MCP's /oauth/authorize, an
// app link); every way of signing in ends with takeSignInReturn(). Password and MFA finish
// on the same page, but Google and Apple leave the site and come back to /auth/oauth/return,
// and a magic link opens from the mail in a new tab, so the path is kept in localStorage
// (shared by the site's tabs) for a while, not only in the URL.

const KEY = 'bb.signInReturn';
const KEEP_MS = 30 * 60 * 1000;   // long enough to find the magic link in the mailbox

/** Only a path on this site: "/x", never "//host", "/\host" or another origin. */
export function safeReturn(path) {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return null;
  if (/^\/(login|register)(\/|\?|$)/.test(path)) return null;
  return path;
}

/**
 * The sign-in and sign-up pages call this with their ?redirect when they open. Without one
 * it forgets an older path, so a sign-in given up on (a desktop connect left in a tab) does
 * not send a later, ordinary sign-in there.
 */
export function rememberSignInReturn(path, now = Date.now()) {
  const safe = safeReturn(path);
  try {
    if (safe) localStorage.setItem(KEY, JSON.stringify({ path: safe, at: now }));
    else localStorage.removeItem(KEY);
  } catch {
    // storage blocked: the password form still has ?redirect in its own URL
  }
}

/** The page to open after signing in (once): the page's own ?redirect, the remembered one, the app. */
export function takeSignInReturn(redirect = null, now = Date.now()) {
  let saved;
  try {
    saved = JSON.parse(localStorage.getItem(KEY) || 'null');
    localStorage.removeItem(KEY);
  } catch {
    saved = null;
  }
  const remembered = saved && now - Number(saved.at) < KEEP_MS ? safeReturn(saved.path) : null;
  return safeReturn(redirect) || remembered || '/app';
}

/** The sign-in page for a person sent away from `location` (react-router's). */
export function signInPathFor(location) {
  const here = `${location.pathname}${location.search || ''}${location.hash || ''}`;
  return safeReturn(here) ? `/login?redirect=${encodeURIComponent(here)}` : '/login';
}
